/**
 * <mb-juntos>: «Añádelo también» (snippets/mb-juntos.liquid).
 * Casillas marcadas por defecto; el total y el texto del botón se recalculan al marcar/desmarcar.
 * Al pulsar: POST /cart/add.js con todas las variantes marcadas y aviso a Horizon con CartLinesUpdateEvent
 * (acción 'update', igual que mb-carrito-complementos.js, para que contador y cajón se repinten sin reabrir el cajón).
 * add_to_cart en dataLayer NO se empuja aquí: assets/mb-datalayer.js ya observa /cart/add y lo emite (empujarlo dos veces duplicaría la medición).
 */

const raiz = () => window.Shopify?.routes?.root ?? '/';
const CABECERAS = { 'Content-Type': 'application/json', Accept: 'application/json' };

/** @type {typeof import('@shopify/events').CartLinesUpdateEvent | null} */
let EventoCarrito = null;
import('@shopify/events')
  .then((modulo) => {
    EventoCarrito = modulo.CartLinesUpdateEvent;
  })
  .catch(() => {
    EventoCarrito = null;
  });

if (!customElements.get('mb-juntos')) {
  class MbJuntos extends HTMLElement {
    /** @type {HTMLInputElement[]} */
    #casillas = [];
    /** @type {HTMLButtonElement | null} */
    #boton = null;
    #ocupado = false;

    connectedCallback() {
      this.#casillas = [...this.querySelectorAll('input[type="checkbox"][data-variante]')].filter(
        (el) => el instanceof HTMLInputElement
      );
      this.#boton = this.querySelector('[data-mb-juntos-boton]');
      this.addEventListener('change', this.#alCambiar);
      this.#boton?.addEventListener('click', this.#alPulsar);
      this.#actualizar();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.#alCambiar);
      this.#boton?.removeEventListener('click', this.#alPulsar);
    }

    #alCambiar = () => {
      this.#limpiarEstado();
      this.#actualizar();
    };

    #alPulsar = () => {
      this.#anadir();
    };

    get #marcadas() {
      return this.#casillas.filter((c) => c.checked);
    }

    /** @param {number} centimos */
    #formatear(centimos) {
      const moneda = this.dataset.moneda || 'EUR';
      return new Intl.NumberFormat('es-ES', { style: 'currency', currency: moneda }).format(centimos / 100);
    }

    #actualizar() {
      const marcadas = this.#marcadas;
      const total = marcadas.reduce((t, c) => t + Number(c.dataset.precio || 0), 0);
      const elTotal = this.querySelector('[data-mb-juntos-total]');
      if (elTotal) elTotal.textContent = this.#formatear(total);
      if (!this.#ocupado) {
        const texto = this.querySelector('[data-mb-juntos-texto]');
        if (texto) texto.textContent = marcadas.length === 1 ? 'Añadir 1' : `Añadir los ${marcadas.length}`;
        if (this.#boton) this.#boton.disabled = marcadas.length === 0;
      }
    }

    /** @param {string} mensaje */
    #decir(mensaje) {
      const estado = this.querySelector('[data-mb-juntos-estado]');
      if (estado) estado.textContent = mensaje;
    }

    #limpiarEstado() {
      this.#decir('');
    }

    async #anadir() {
      const marcadas = this.#marcadas;
      if (this.#ocupado || !marcadas.length || !this.#boton) return;
      this.#ocupado = true;
      this.#boton.disabled = true;
      this.#boton.setAttribute('aria-busy', 'true');
      const texto = this.querySelector('[data-mb-juntos-texto]');
      if (texto) texto.textContent = 'Añadiendo…';
      this.#decir('');

      const items = marcadas.map((c) => ({ id: Number(c.dataset.variante), quantity: 1 }));
      const diferido = EventoCarrito?.createPromise?.();
      if (diferido && EventoCarrito) {
        this.#boton.dispatchEvent(
          new EventoCarrito({
            action: 'update',
            context: 'standard-action',
            lines: items.map((i) => ({ id: String(i.id), quantity: 1 })),
            promise: diferido.promise,
          })
        );
        diferido.promise.catch(() => {});
      }

      try {
        const respuesta = await fetch(`${raiz()}cart/add.js`, {
          method: 'POST',
          headers: CABECERAS,
          body: JSON.stringify({ items }),
        });
        const datos = await respuesta.json();
        if (!respuesta.ok) throw new Error(datos?.description || datos?.message || 'No se pudo añadir');

        const carrito = await (await fetch(`${raiz()}cart.js`, { headers: { Accept: 'application/json' } })).json();
        window.mbCarrito = carrito;
        document.dispatchEvent(new CustomEvent('mb:carrito', { detail: carrito }));
        this.#decir('Añadido al carrito');

        diferido?.resolve({
          cart: EventoCarrito?.createCartFromAjaxResponse?.(carrito),
          detail: {
            items: carrito.items,
            itemCount: carrito.item_count,
            source: 'mb-juntos',
            sourceId: 'mb-juntos',
            didError: false,
          },
        });
      } catch (error) {
        diferido?.reject(error);
        this.#decir('No se ha podido añadir. Inténtalo de nuevo.');
      } finally {
        this.#ocupado = false;
        this.#boton.removeAttribute('aria-busy');
        this.#actualizar();
      }
    }
  }

  customElements.define('mb-juntos', MbJuntos);
}
